import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPteCoreWritingTasks, submitPteCoreWriting } from '../../api';
import { WritingTask as IWritingTask, WritingSubmission } from '../types';
import PteCoreWritingScore from './PteCoreWritingScore.tsx';
import './ptecorereading.css';
import { BookOpen, AlertCircle, Clipboard, Scissors, Copy as CopyIcon } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';


const getStoredUser = (reduxUser: any) => {
    if (reduxUser?.result) return reduxUser.result;
    try {
        const localUser = JSON.parse(localStorage.getItem("user_info") as string);
        if (localUser?.result) return localUser.result;
    } catch { }
    return null;
};

interface WritingTaskProps {
    task: IWritingTask;
    value: string;
    onChange: (text: string) => void;
    onNext: () => void;
    onSubmit: () => void;
    isLast: boolean;
    isSubmitting: boolean;
}

const WritingTask: React.FC<WritingTaskProps> = ({
    task,
    value,
    onChange,
    onNext,
    onSubmit,
    isLast,
    isSubmitting
}) => {
    const [timeLeft, setTimeLeft] = useState((task.timeLimit || 10) * 60);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Derived word count from prop
    const wordCount = value.trim() === '' ? 0 : value.trim().split(/\s+/).length;

    useEffect(() => {
        setTimeLeft((task.timeLimit || 10) * 60);
    }, [task]);

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [task]);

    const handleCut = async () => {
        if (!textareaRef.current) return;
        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const selectedText = value.substring(start, end);

        if (selectedText) {
            await navigator.clipboard.writeText(selectedText);
            const newText = value.substring(0, start) + value.substring(end);
            onChange(newText);
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(start, start);
        }
    };

    const handleCopy = async () => {
        if (!textareaRef.current) return;
        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const selectedText = value.substring(start, end);
        if (selectedText) {
            await navigator.clipboard.writeText(selectedText);
            textareaRef.current.focus();
        }
    };

    const handlePaste = async () => {
        try {
            const textToPaste = await navigator.clipboard.readText();
            if (!textareaRef.current) return;

            const start = textareaRef.current.selectionStart;
            const end = textareaRef.current.selectionEnd;

            const newText = value.substring(0, start) + textToPaste + value.substring(end);
            onChange(newText);
            // Defer cursor update to next tick to allow React to render
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.setSelectionRange(start + textToPaste.length, start + textToPaste.length);
                }
            }, 0);
        } catch (err) {
            console.error("Paste failed", err);
        }
    };

    const displayTitle = task.title || (task.type === 'summary' ? `Summarize Written Text - Question ${task.Task}` : `Write Email - Question ${task.Task}`);

    return (
        <div className="flex flex-col h-full max-w-5xl mx-auto font-sans">
            <header className="ptest-header">
                <div className="ptest-title">
                    <h2 className="passage-title">{displayTitle}</h2>
                </div>
                <div className="right-controls">
                    <div className="timer">
                        <span className="timer-emoji">⏱</span> {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                    </div>
                </div>
            </header>

            <section className="questions-wrapper">
                <section className="passage-wrapper">
                    <div className="passage-outer">
                        <div className="passage-inner">
                            {/* Question Text */}
                            <div
                                dangerouslySetInnerHTML={{ __html: task.question }}
                                className="passage-body w-full"
                            />

                            {/* Input Area */}
                            <textarea
                                ref={textareaRef}
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                                className="block box-border h-32 p-3 border border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-gray-800 text-base resize-none shadow-inner mt-4"
                                style={{ width: '100%' }}
                                spellCheck={false}
                            />

                            {/* Toolbar - Below Textarea matching screenshot */}
                            <div className="flex justify-between items-center mt-2">
                                <button
                                    onClick={handleCut}
                                    className="flex items-center justify-center gap-1 w-20 py-1 bg-gray-50 border border-gray-400 rounded-sm shadow-sm hover:bg-gray-100 active:bg-gray-200 transition-colors text-sm text-gray-800 font-medium"
                                >
                                    <Scissors className="w-3.5 h-3.5" /> Cut
                                </button>

                                <button
                                    onClick={handleCopy}
                                    className="flex items-center justify-center gap-1 w-20 py-1 bg-gray-50 border border-gray-400 rounded-sm shadow-sm hover:bg-gray-100 active:bg-gray-200 transition-colors text-sm text-gray-800 font-medium"
                                >
                                    <CopyIcon className="w-3.5 h-3.5" /> Copy
                                </button>

                                <button
                                    onClick={handlePaste}
                                    className="flex items-center justify-center gap-1 w-20 py-1 bg-gray-50 border border-gray-400 rounded-sm shadow-sm hover:bg-gray-100 active:bg-gray-200 transition-colors text-sm text-gray-800 font-medium"
                                >
                                    <Clipboard className="w-3.5 h-3.5" /> Paste
                                </button>
                            </div>

                            {/* Word Count */}
                            <div className="mt-2 text-sm font-semibold text-gray-700">
                                Total Word Count: {wordCount}
                            </div>
                        </div>
                    </div>
                </section>
            </section>

            {/* Navigation Row - using same structure as header for right alignment */}
            <div className="ptest-header" style={{ marginTop: '20px' }}>
                <div></div>
                <div className="right-controls flex gap-4">
                    <button className="btn btn-ghost" disabled>
                        ← Previous Task
                    </button>
                    <button
                        onClick={isLast ? onSubmit : onNext}
                        disabled={isSubmitting}
                        className="btn btn-primary"
                    >
                        {isSubmitting ? 'Submitting...' : (isLast ? 'Submit' : 'Next Task →')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PteCoreWritingTest: React.FC<{ isFullTest?: boolean; onSectionComplete?: () => void }> = ({ isFullTest, onSectionComplete }) => {
    const reduxUser = useSelector((state: any) => state.authReducer?.authData);
    const user = getStoredUser(reduxUser);
    const { testNumber } = useParams<{ testNumber: string }>();
    const navigate = useNavigate();

    // Use testNumber from URL, default to 1 if missing (though route should enforce it)
    const currentTestNumber = Number(testNumber) || 1;

    const [tasks, setTasks] = useState<IWritingTask[]>([]);
    const [currentTaskIndex, setCurrentTaskIndex] = useState(0);

    // State for batch submission
    const [userAnswers, setUserAnswers] = useState<Record<string, string>>({});
    const [results, setResults] = useState<WritingSubmission[]>([]);
    const [testCompleted, setTestCompleted] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load tasks when test number changes
    useEffect(() => {
        const loadTasks = async () => {
            setLoading(true);
            setError(null);
            setTasks([]);
            setResults([]);
            setTestCompleted(false);
            setUserAnswers({});
            setCurrentTaskIndex(0);
            try {
                const response = await fetchPteCoreWritingTasks(currentTestNumber);
                const data = response.data;

                if (!Array.isArray(data)) {
                    console.error("Expected array from API but got:", data);
                    throw new Error(`Invalid data format: Expected array, got ${typeof data}. Check if API URL matches backend.`);
                }

                if (data.length === 0) {
                    setError(`No tasks found for Test ${currentTestNumber}`);
                } else {
                    // Map backend data to frontend interface if needed, or assume API returns correct shape
                    // Based on previous plan, we might need mapping here if API returns raw DB objects
                    const mappedTasks = data.map((item: any) => ({
                        _id: item._id,
                        Test_number: item.testNumber,
                        Task: item.taskNumber,
                        type: item.component?.toLowerCase().includes('summary') ? 'summary' : 'email',
                        wordLimit: item.wordLimit,
                        timeLimit: item['time(in minutes)'] ? item['time(in minutes)'] / 60 : 10, // DB has seconds (600), convert to minutes
                        question: item.question,
                        title: item.taskTitle
                    }));
                    setTasks(mappedTasks);
                }
            } catch (err: any) {
                setError(err.message || "Failed to load tasks");
            } finally {
                setLoading(false);
            }
        };
        loadTasks();
    }, [currentTestNumber]);

    const handleAnswerUpdate = (val: string) => {
        if (!tasks[currentTaskIndex]) return;
        setUserAnswers(prev => ({
            ...prev,
            [tasks[currentTaskIndex]._id]: val
        }));
    };

    const handleFinalSubmit = async () => {
        setLoading(true);
        try {
            // Submit all answers sequentially or parallel
            const promises = tasks.map(t => {
                const txt = userAnswers[t._id] || '';
                return submitPteCoreWriting(t._id, txt, user?.name || "Guest User", user?._id || user?.id);
            });

            const res = await Promise.all(promises);
            // Extract data from axios response
            const submissions = res.map(r => ({
                _id: r.data.submissionId,
                task: r.data.task || r.config.data ? JSON.parse(r.config.data).taskId : '',
                studentName: user?.name || "Guest User",
                responseText: r.data.responseText || '', // API might not return text back, careful
                wordCount: (r.data.responseText || '').split(/\s+/).length,
                autoScore: r.data.autoScore,
                createdAt: new Date().toISOString()
            }));
            setResults(submissions);
            setTestCompleted(true);
            setCurrentTaskIndex(0); // Start showing results from first question
        } catch (e: any) {
            setError(e.message || "Submission failed");
        } finally {
            setLoading(false);
        }
    };

    const goToNextQuestion = () => {
        if (currentTaskIndex < tasks.length - 1) {
            setCurrentTaskIndex(prev => prev + 1);
        }
    };

    const handleNextFlow = () => {
        if (isFullTest && onSectionComplete) {
            onSectionComplete();
        } else {
            navigate('/pte/dashboardpte');
        }
    };

    const handleRetry = () => {
        // Reset everything for a fresh attempt
        setTestCompleted(false);
        setResults([]);
        setUserAnswers({});
        setCurrentTaskIndex(0);
    };

    return (
        <div className="min-h-screen flex flex-col">
            {/* Top Navigation Bar */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-600 p-2 rounded text-white">
                            <BookOpen size={20} />
                        </div>
                        <div className="ptest-title">
                            <h1>PTE Core Writing Practice</h1>
                        </div>
                    </div>

                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-grow bg-gray-50 p-4 md:p-8">
                {loading ? (
                    <div className="flex justify-center items-center h-64" >
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                ) : error ? (
                    <div className="max-w-2xl mx-auto mt-10 bg-red-50 border border-red-200 rounded-md p-6 flex items-start gap-4 text-red-700">
                        <AlertCircle className="flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-lg">Error Loading Test</h3>
                            <p>{error}</p>
                            <p className="text-sm mt-2 text-red-500">
                                Note: Ensure your backend is running at {API_BASE_URL}
                            </p>
                        </div>
                    </div>
                ) : tasks.length > 0 ? (
                    <>
                        {testCompleted && results.length > 0 ? (
                            <PteCoreWritingScore
                                results={results.map((r, idx) => ({
                                    taskId: r.task,
                                    taskNumber: idx + 1,
                                    taskTitle: tasks[idx]?.title || `Task ${idx + 1}`,
                                    responseText: r.responseText,
                                    autoScore: r.autoScore
                                }))}
                                onRetry={handleRetry}
                                onNext={handleNextFlow}
                                nextLabel={isFullTest ? "Proceed to Reading Section" : "Return to Dashboard"}
                            />
                        ) : (
                            <WritingTask
                                task={tasks[currentTaskIndex]}
                                value={userAnswers[tasks[currentTaskIndex]?._id] || ''}
                                onChange={handleAnswerUpdate}
                                onNext={goToNextQuestion}
                                onSubmit={handleFinalSubmit}
                                isLast={currentTaskIndex === tasks.length - 1}
                                isSubmitting={loading}
                            />
                        )}
                    </>
                ) : (
                    <div className="text-center mt-20 text-gray-500">
                        Select a test number to begin.
                    </div>
                )}
            </main>
        </div>
    );
};

export default PteCoreWritingTest;
